import { useNavigate, useParams } from 'react-router-dom';
import { CashbackTransaction } from '../../../../types';
import { useAppDispatch } from '../../../../hooks';
import React, { useMemo, useState } from 'react';
import { Table } from '../../../components';
import { cashbackColumns } from '../../../../constants';
import { getCashbackTransactionById } from '../../../../store/slices';
import { SvgCoupons } from '../../../../assets/svg/general';

const Cashback = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { cashbackId } = useParams();
  const [cashbackTransactions, setCashbackTransactions] = useState<CashbackTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  React.useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const result = await dispatch(getCashbackTransactionById({ id: parseInt(cashbackId!) }));
        if (result.type.endsWith('/fulfilled')) setCashbackTransactions(result.payload as CashbackTransaction[]);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [dispatch, cashbackId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return cashbackTransactions;
    const q = search.toLowerCase();
    return cashbackTransactions.filter((t: any) =>
      t.user?.firstName?.toLowerCase().includes(q) ||
      t.user?.lastName?.toLowerCase().includes(q) ||
      t.user?.email?.toLowerCase().includes(q) ||
      t.slug?.toLowerCase().includes(q)
    );
  }, [cashbackTransactions, search]);

  return (
    <div className="flex flex-col h-full p-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm mb-4">
        <span className="text-lg leading-none">&lsaquo;</span>
        <span className="medium">Back</span>
      </button>

      <h1 className="semibold text-xl text-text mb-6">Cashback transactions</h1>

      <div className="flex-1 flex flex-col min-h-0 bg-white rounded-xl border border-slate-medium">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-text-placeholder" width={16} height={16} viewBox="0 0 16 16" fill="none">
                <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9 pr-4 py-2.5 text-sm border border-slate-medium rounded-lg bg-white placeholder:text-text-placeholder focus:outline-none focus:border-primary w-64" />
            </div>
            <button className="flex items-center gap-2 px-4 py-2.5 text-sm medium border border-slate-medium rounded-lg bg-white hover:bg-gray-50 transition-colors">
              <svg width={16} height={16} viewBox="0 0 16 16" fill="none"><path d="M2 4H14M4 8H12M6 12H10" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" /></svg>
              Filter
            </button>
          </div>
          <button className="flex items-center gap-2 px-4 py-2.5 text-sm medium border border-slate-medium rounded-lg bg-white hover:bg-gray-50 transition-colors">
            <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
              <path d="M2.66699 10.667V12.667C2.66699 13.0206 2.80747 13.3598 3.05752 13.6098C3.30756 13.8598 3.64671 14.0003 4.00033 14.0003H12.0003C12.354 14.0003 12.6931 13.8598 12.9431 13.6098C13.1932 13.3598 13.3337 13.0206 13.3337 12.667V10.667M10.667 5.33366L8.00033 2.66699M8.00033 2.66699L5.33366 5.33366M8.00033 2.66699V10.667" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Export
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><p className="text-text-placeholder text-sm">Loading...</p></div>
        ) : cashbackTransactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 rounded-xl bg-slate-mild flex items-center justify-center mb-4"><SvgCoupons className="w-7 h-7 text-text-placeholder" /></div>
            <h3 className="semibold text-base text-text mb-1">No transactions</h3>
            <p className="text-sm text-text-placeholder">When cashback transactions occur, they will show here</p>
          </div>
        ) : (
          <Table
            columns={cashbackColumns}
            data={filtered}
            tableFor="transactions"
            showSearch={false}
            containerClassName="mt-0 flex-1 flex flex-col min-h-0"
          />
        )}
      </div>
    </div>
  );
};

export default Cashback;
